import { View } from "@frui.ts/views";
import type { Container } from "inversify";
import { reaction } from "mobx";
import React from "react";
import ReactDOM from "react-dom";
import localizationProvider from "./localization/localizationProvider";
import InitializationService from "./services/initializationService";
import LocalizationService from "./services/localizationService";
import SecurityService from "./services/securityService";
import LoginViewModel from "./viewModels/loginViewModel";
import RootViewModel from "./viewModels/rootViewModel";
import ModalService from "./services/modalService";
import EnumService from "./services/enumService";

function renderViewModel(viewModels: Array<unknown>) {
  ReactDOM.render(
    <>
      {viewModels.map((item, index) => (
        <View key={index} vm={item} useLifecycle />
      ))}
    </>,
    document.getElementById("root")
  );
}

function renderRoot(container: Container, initializationService: InitializationService, securityService: SecurityService) {
  const hashStoreName = "hashRestore";
  if (!initializationService.isPreLoginInitialized) {
    if (!sessionStorage.getItem(hashStoreName)) {
      sessionStorage.setItem(hashStoreName, window.location.hash);
    }
    if (!securityService.isLogged) {
      void initializationService.preLoginInitialize();
    }
    renderViewModel([initializationService]);
    return;
  }

  const modalService = container.get(ModalService);
  if (!securityService.isLogged) {
    const loginViewModel = container.get(LoginViewModel);
    renderViewModel([modalService, loginViewModel]);
    return;
  }

  if (!initializationService.isPostLoginInitialized) {
    void initializationService.postLoginInitialize();
    renderViewModel([initializationService]);
    return;
  }

  const restoreHash = sessionStorage.getItem(hashStoreName);
  if (restoreHash) {
    window.location.hash = restoreHash;
    sessionStorage.removeItem(hashStoreName);
  }

  const rootViewModel = container.get(RootViewModel);
  renderViewModel([modalService, rootViewModel]);
}

export default function runApp(container: Container) {
  const initializationService = container.get<InitializationService>(InitializationService);
  const securityService = container.get<SecurityService>(SecurityService);
  localizationProvider.initialize(container.get(LocalizationService), container.get(EnumService));

  reaction(
    () => [initializationService.isPreLoginInitialized, initializationService.isPostLoginInitialized, securityService.isLogged],
    () => renderRoot(container, initializationService, securityService),
    { fireImmediately: true }
  );
}
